import { normalizeInvoiceRole } from './invoiceLedger';

export const PAYMENT_TERMS_PRESETS = [0, 7, 14, 30] as const;

export type PaymentTermsPreset = (typeof PAYMENT_TERMS_PRESETS)[number] | 'custom';

export const DEPOSIT_CLEARED_FUNDS_NOTE =
  'Your order will be placed and your installation booking secured once cleared funds are received.';

const DEPOSIT_RECEIPT_WORDING =
  `Payment due on receipt. ${DEPOSIT_CLEARED_FUNDS_NOTE}`;

export function todayDateOnly(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function toDateOnly(value?: string | null): string | null {
  if (!value) return null;
  const match = String(value).trim().match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return todayDateOnly(parsed);
}

export function dateOnlyToIssuedAt(dateOnly: string): string {
  return `${dateOnly}T12:00:00.000Z`;
}

export function addCalendarDays(dateOnly: string, days: number): string {
  const [year, month, day] = dateOnly.split('-').map(Number);
  const next = new Date(year, month - 1, day);
  next.setDate(next.getDate() + Number(days || 0));
  return todayDateOnly(next);
}

export function defaultPaymentTermsInDays(role?: string | null): number {
  return normalizeInvoiceRole(role) === 'deposit' ? 0 : 7;
}

export function normalizePaymentTermsInDays(value?: number | null, role?: string | null): number {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return Math.floor(value);
  }
  return defaultPaymentTermsInDays(role);
}

export function invoiceDueDate(invoiceDate: string, paymentTermsInDays: number): string {
  return addCalendarDays(invoiceDate, paymentTermsInDays);
}

export function paymentTermsLabel(days: number): string {
  if (days === 0) return 'Due on receipt';
  if (days === 7) return '7 days';
  if (days === 14) return '14 days';
  if (days === 30) return '30 days';
  return `Custom (${days} days)`;
}

export function paymentTermsDaysDisplay(days: number): string {
  if (days <= 0) return 'Due on receipt';
  return days === 1 ? '1 Day' : `${days} Days`;
}

export function paymentTermsPreset(days: number): PaymentTermsPreset {
  return (PAYMENT_TERMS_PRESETS as readonly number[]).includes(days)
    ? days as (typeof PAYMENT_TERMS_PRESETS)[number]
    : 'custom';
}

export function invoicePaymentWording(args: {
  invoiceRole?: string | null;
  paymentTermsInDays?: number | null;
}): string {
  const days = normalizePaymentTermsInDays(args.paymentTermsInDays, args.invoiceRole);
  if (days === 0) return DEPOSIT_RECEIPT_WORDING;
  if (days === 7) return 'Payment due within 7 days.';
  return `Payment due within ${days} days.`;
}

export function formatDateGb(dateOnly: string): string {
  const [year, month, day] = dateOnly.split('-');
  if (!year || !month || !day) return dateOnly;
  return `${day}/${month}/${year}`;
}

export function formatInvoiceDateLong(dateOnly: string): string {
  const [year, month, day] = dateOnly.split('-').map(Number);
  if (!year || !month || !day) return dateOnly;
  const date = new Date(year, month - 1, day);
  const suffix = (n: number) => {
    const j = n % 10;
    const k = n % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };
  const monthName = date.toLocaleString('en-GB', { month: 'long' });
  return `${day}${suffix(day)} ${monthName} ${year}`;
}

/** Dates chosen in Edit payment terms (or set on FreeAgent issue) are stored at noon UTC. */
const CHOSEN_ISSUE_DATE = /T12:00:00(\.0+)?(Z|\+00(:?00)?)$/;

export function issuedAtLooksLikeCreationDefault(
  issuedAt?: string | null,
  createdAt?: string | null,
): boolean {
  if (issuedAt && CHOSEN_ISSUE_DATE.test(String(issuedAt).trim().replace(' ', 'T'))) return false;
  const issued = toDateOnly(issuedAt);
  const created = toDateOnly(createdAt);
  if (!issued || !created) return !issued;
  return issued === created;
}

export function invoicePaymentScheduleLocked(args: {
  freeAgentUrl?: string | null;
  freeagentInvoiceUrl?: string | null;
}): boolean {
  return Boolean(String(args.freeAgentUrl || args.freeagentInvoiceUrl || '').trim());
}

/** Deposits default to due on receipt, but retrospective deposits need a back-dated invoice date. */
export function invoicePaymentTermsEditable(invoiceRole?: string | null): boolean {
  const role = normalizeInvoiceRole(invoiceRole);
  return role === 'deposit' || role === 'works' || role === 'variation';
}

export function resolveInvoiceIssueDate(args: {
  issuedAt?: string | null;
  createdAt?: string | null;
  locked?: boolean;
  now?: Date;
}): string {
  const stored = toDateOnly(args.issuedAt);
  if (args.locked && stored) return stored;
  if (stored && !issuedAtLooksLikeCreationDefault(args.issuedAt, args.createdAt)) return stored;
  return todayDateOnly(args.now);
}

export type InvoicePaymentSchedule = {
  invoiceDate: string;
  paymentTermsInDays: number;
  dueDate: string;
  label: string;
  wording: string;
};

export function resolveInvoicePaymentSchedule(args: {
  invoiceRole?: string | null;
  issuedAt?: string | null;
  createdAt?: string | null;
  paymentTermsInDays?: number | null;
  locked?: boolean;
  now?: Date;
}): InvoicePaymentSchedule {
  const invoiceDate = resolveInvoiceIssueDate(args);
  const paymentTermsInDays = normalizePaymentTermsInDays(args.paymentTermsInDays, args.invoiceRole);
  return {
    invoiceDate,
    paymentTermsInDays,
    dueDate: invoiceDueDate(invoiceDate, paymentTermsInDays),
    label: paymentTermsLabel(paymentTermsInDays),
    wording: invoicePaymentWording({
      invoiceRole: args.invoiceRole,
      paymentTermsInDays,
    }),
  };
}

export function freeAgentInvoiceDatePayload(schedule: InvoicePaymentSchedule): {
  datedOn: string;
  paymentTermsInDays: number;
  dueOn: string;
} {
  return {
    datedOn: schedule.invoiceDate,
    paymentTermsInDays: schedule.paymentTermsInDays,
    dueOn: schedule.dueDate,
  };
}
